import { computed, ComputedRef, unref } from 'vue';
import type { Route } from 'vue-router';
import {
  TAppVersionPagePayload,
  TAppVersionReportPayload,
  TSingleScanPagePayload,
  TSingleScanReportPayload,
  TTestingMethod,
} from '../models/types';
import { DEFAULT_TESTING_METHOD, REPORT_PAGE_LIMIT, VALID_TESTING_METHODS } from '../models/consts';
import { getQueryParamValue } from '@/_core/shared/utils/getQueryParamValue';

export function parseTestingMethod(value: string): TTestingMethod {
  return VALID_TESTING_METHODS.includes(value as TTestingMethod)
    ? (value as TTestingMethod)
    : DEFAULT_TESTING_METHOD;
}

export function useActiveFolderName(routeRef: ComputedRef<Route | undefined>): ComputedRef<string> {
  return computed(() =>
    getQueryParamValue(routeRef.value?.query?.folderName, 'All Vulnerabilities')
  );
}

/* eslint-disable no-redeclare -- TypeScript function overload signatures */
export function useReportPayload(
  type: 'single-scan',
  routeRef: ComputedRef<Route | undefined>
): ComputedRef<TSingleScanReportPayload | null>;

export function useReportPayload(
  type: 'app-version',
  routeRef: ComputedRef<Route | undefined>
): ComputedRef<TAppVersionReportPayload | null>;

export function useReportPayload(
  type: 'single-scan' | 'app-version',
  routeRef: ComputedRef<Route | undefined>
) {
  const route = unref(routeRef);

  const folderNameRef = computed(() => {
    if (!route) {
      return 'All Vulnerabilities';
    }

    if (!route.query.folderName) {
      return 'All Vulnerabilities';
    }

    if (Array.isArray(route.query.folderName)) {
      return route.query.folderName[0] || 'All Vulnerabilities';
    }

    return route.query.folderName;
  });

  return computed(() => {
    if (!route) {
      return null;
    }

    const params = route.params;

    if (type === 'single-scan' && params.jobId) {
      const result: TSingleScanReportPayload = {
        jobId: params.jobId,
        folderName: folderNameRef.value,
      };

      return result;
    }

    if (type === 'app-version' && params.integrationId && params.branchName && params.versionId) {
      const result: TAppVersionReportPayload = {
        integrationId: params.integrationId,
        folderName: folderNameRef.value,
        branchName: params.branchName,
        versionId: params.versionId,
      };

      return result;
    }

    return null;
  });
}
/* eslint-enable no-redeclare */

export function useSingleScanPagePayload(
  routeRef: ComputedRef<Route | undefined>,
  activeFolderName: ComputedRef<string>
): ComputedRef<TSingleScanPagePayload | null> {
  return computed(() => {
    const params = unref(routeRef)?.params;
    const query = unref(routeRef)?.query;
    if (!params?.jobId) return null;

    const page = Number(query?.page) || 1;
    const testingMethod = parseTestingMethod(
      getQueryParamValue(query?.testingMethod, DEFAULT_TESTING_METHOD)
    );

    return {
      jobId: params.jobId,
      folderName: activeFolderName.value,
      page,
      limit: REPORT_PAGE_LIMIT,
      filters: {
        testingMethod,
        vulnerabilityCategoriesIds: [],
        complianceRequirementId:
          getQueryParamValue(query?.complianceRequirementId, '') || undefined,
        complianceRequirementItemId:
          getQueryParamValue(query?.complianceRequirementItemId, '') || undefined,
      },
    };
  });
}

export function useAppVersionPagePayload(
  routeRef: ComputedRef<Route | undefined>,
  activeFolderName: ComputedRef<string>
): ComputedRef<TAppVersionPagePayload | null> {
  return computed(() => {
    const params = unref(routeRef)?.params;
    const query = unref(routeRef)?.query;

    if (!params?.integrationId || !params?.branchName || !params?.versionId) return null;

    const page = Number(query?.page) || 1;
    const testingMethod = parseTestingMethod(
      getQueryParamValue(query?.testingMethod, DEFAULT_TESTING_METHOD)
    );

    return {
      integrationId: params.integrationId,
      branchName: params.branchName,
      versionId: params.versionId,
      folderName: activeFolderName.value,
      page,
      limit: REPORT_PAGE_LIMIT,
      filters: {
        testingMethod,
        vulnerabilityCategoriesIds: [],
        complianceRequirementId:
          getQueryParamValue(query?.complianceRequirementId, '') || undefined,
        complianceRequirementItemId:
          getQueryParamValue(query?.complianceRequirementItemId, '') || undefined,
      },
    };
  });
}
